import { useCallback, useEffect, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Wallet, Plus, Pencil, Trash2, CheckCircle2, Clock } from "lucide-react";
import { METHOD_LABELS } from "@/components/PaymentDialog";
import { PaymentEntryDialog } from "@/components/PaymentEntryDialog";
import { useHideValues } from "@/components/HideValues";

const fmtD = (d) => (d ? d.split("-").reverse().join("/") : "");
const body = (e, patch = {}) => ({ method: e.method, amount: e.amount, date: e.date, paid: e.paid, card_installments: e.card_installments, note: e.note, machine_id: e.machine_id, auth_code: e.auth_code, ...patch });

function Tile({ label, value, tone, testid }) {
  const { hide } = useHideValues();
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3">
      <div className="text-[10px] font-mono-caps uppercase tracking-widest text-zinc-500">{label}</div>
      <div data-testid={testid} className={`mt-1 font-display text-lg font-black tabular-nums ${tone}`}>{hide ? "R$ •••" : brl(value)}</div>
    </div>
  );
}

function EntryRow({ e, onToggle, onEdit, onDelete }) {
  const { hide } = useHideValues();
  const m = (v) => (hide ? "•••" : brl(v));
  return (
    <li data-testid={`payment-entry-${e.id}`} className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-3 flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-bold text-zinc-100 truncate">{METHOD_LABELS[e.method] || e.method}{e.card_installments > 1 ? ` · ${e.card_installments}x` : ""}</span>
          <span className="text-sm font-bold tabular-nums text-zinc-100">{m(e.amount)}</span>
        </div>
        <div className="text-[11px] text-zinc-500 mt-0.5">{fmtD(e.date)}{e.note ? ` · ${e.note}` : ""}{e.auth_code ? ` · Autent. ${e.auth_code}` : ""}</div>
        {e.fee > 0 && <div data-testid={`payment-entry-fee-${e.id}`} className="text-[11px] text-sky-300 mt-0.5">Taxa {e.fee_rate}% − {m(e.fee)} · líquido {m(e.net)}</div>}
        <div className="mt-2 flex items-center gap-2">
          <button type="button" data-testid={`payment-entry-toggle-${e.id}`} onClick={onToggle} className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-bold uppercase border transition-colors ${e.paid ? "border-green-700 text-green-300 bg-green-950/40" : "border-red-900 text-red-300 bg-red-950/30"}`}>
            {e.paid ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}{e.paid ? "Recebido" : "A receber"}
          </button>
          <button type="button" data-testid={`payment-entry-edit-${e.id}`} onClick={onEdit} className="h-7 w-7 inline-flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800" aria-label="Editar"><Pencil className="h-3.5 w-3.5" /></button>
          <button type="button" data-testid={`payment-entry-delete-${e.id}`} onClick={onDelete} className="h-7 w-7 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-red-300 hover:bg-red-950/40" aria-label="Excluir"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    </li>
  );
}

export function PaymentsBlock({ source, id, canEdit = true, onChanged, refreshKey, liveTotal }) {
  const [data, setData] = useState(null);
  const [dlg, setDlg] = useState(null);
  const load = useCallback(() => api.get(`/payments/${source}/${id}`).then(({ data: d }) => setData(d)).catch(() => {}), [source, id]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const apply = (d) => { setData(d); onChanged?.(); };
  const run = async (fn, ok) => { try { const { data: d } = await fn(); apply(d); if (ok) toast.success(ok); } catch (err) { toast.error(formatApiError(err)); } };
  const toggle = (e) => run(() => api.patch(`/payments/${source}/${id}/${e.id}`, body(e, { paid: !e.paid })), e.paid ? "Marcado como a receber" : "Marcado como recebido");
  const del = (e) => window.confirm("Excluir este pagamento? O lançamento no Financeiro também sai.") && run(() => api.delete(`/payments/${source}/${id}/${e.id}`), "Pagamento excluído");
  const save = async (payload) => {
    const editing = dlg?.id;
    await run(() => (editing ? api.patch(`/payments/${source}/${id}/${dlg.id}`, payload) : api.post(`/payments/${source}/${id}`, payload)), editing ? "Pagamento atualizado" : "Pagamento lançado");
    setDlg(null);
  };
  if (!data) return null;
  const showTotal = liveTotal != null ? liveTotal : data.total;
  const showPending = Math.max(0, Math.round((showTotal - (data.received || 0)) * 100) / 100);
  return (
    <div data-testid="payments-block" className="rounded-2xl border border-card2-border bg-appbg-soft/40 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-mono-caps text-zinc-500 uppercase tracking-widest"><Wallet className="h-4 w-4" /> Pagamentos</div>
        {canEdit && <Button data-testid="payment-entry-add-btn" size="sm" onClick={() => setDlg({})} className="h-8 bg-green-brand hover:bg-green-brandDark text-white font-bold"><Plus className="h-4 w-4 mr-1" /> Adicionar pagamento</Button>}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Tile label="Valor total" value={showTotal} tone="text-zinc-100" testid="payments-total" />
        <Tile label="Recebido" value={data.received} tone="text-green-neon" testid="payments-received" />
        <Tile label="Falta receber" value={showPending} tone="text-red-400" testid="payments-pending" />
      </div>
      {data.unscheduled > 0.009 && data.total > 0 && <p data-testid="payments-unscheduled" className="text-[11px] text-amber-300">Ainda sem lançamento: {brl(data.unscheduled)}</p>}
      {data.entries.length ? (
        <ul className="space-y-2">{data.entries.map((e) => <EntryRow key={e.id} e={e} onToggle={() => canEdit && toggle(e)} onEdit={() => canEdit && setDlg(e)} onDelete={() => canEdit && del(e)} />)}</ul>
      ) : <p className="text-xs text-zinc-500">Nenhum pagamento lançado. Lance a entrada e depois as parcelas, uma a uma ou divididas.</p>}
      {dlg && <PaymentEntryDialog entry={dlg.id ? dlg : null} suggested={data.unscheduled} onClose={() => setDlg(null)} onSave={save} />}
    </div>
  );
}
